import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { emitToast } from '../../context/toast';
import {
  navigateWompiCheckout,
  openWompiCheckoutPlaceholder,
} from '../../utils/paymentMethods';

function formatCop(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n);
}

function monthsLabel(months) {
  const m = Number(months) || 1;
  if (m === 1) return '1 mes';
  if (m === 2) return '2 meses (bimestral)';
  if (m === 3) return '3 meses (trimestral)';
  if (m === 6) return '6 meses (semestral)';
  if (m === 12) return '12 meses (anual)';
  return `${m} meses`;
}

export default function TrainingCoachPaymentPanel({ onBack }) {
  const [clients, setClients] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const availablePlans = useMemo(() => {
    const list = Array.isArray(pricing?.available_plans) ? pricing.available_plans : [];
    return list.filter((p) => Number(p.precio_mensual) > 0 || p.nombre);
  }, [pricing?.available_plans]);

  const activePlan = useMemo(() => {
    if (!availablePlans.length) {
      return pricing?.plan_nombre
        ? {
          nombre: pricing.plan_nombre,
          precio_mensual: pricing.unit_price_cop,
          cycles_count: pricing.cycles_count || 1,
        }
        : null;
    }
    return availablePlans.find((p) => p.nombre === selectedPlan) || availablePlans[0];
  }, [availablePlans, selectedPlan, pricing]);

  const unitPrice = Number(activePlan?.precio_mensual) || Number(pricing?.unit_price_cop) || 0;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/coach/pending-clients');
      setClients(r.data?.data || []);
      const nextPricing = r.data?.pricing || null;
      setPricing(nextPricing);
      const plans = Array.isArray(nextPricing?.available_plans) ? nextPricing.available_plans : [];
      setSelectedPlan((prev) => {
        if (prev && plans.some((p) => p.nombre === prev)) return prev;
        if (nextPricing?.plan_nombre && plans.some((p) => p.nombre === nextPricing.plan_nombre)) {
          return nextPricing.plan_nombre;
        }
        return plans[0]?.nombre || nextPricing?.plan_nombre || '';
      });
    } catch {
      emitToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudieron cargar clientes pendientes',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const coachPayClients = useMemo(
    () => clients.filter((c) => c.payment_responsibility !== 'client'),
    [clients],
  );

  const clientDirectClients = useMemo(
    () => clients.filter((c) => c.payment_responsibility === 'client'),
    [clients],
  );

  const totalSelected = useMemo(
    () => selected.length * unitPrice,
    [selected.length, unitPrice],
  );

  const toggle = (userId) => {
    setSelected((prev) => (
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    ));
  };

  const payWompi = async () => {
    if (!selected.length) {
      emitToast({
        type: 'warning',
        title: 'Selección',
        message: 'Selecciona al menos un cliente',
      });
      return;
    }
    if (!activePlan?.nombre) {
      emitToast({
        type: 'warning',
        title: 'Plan',
        message: 'Selecciona un plan de activación',
      });
      return;
    }
    if (!(unitPrice > 0)) {
      emitToast({
        type: 'error',
        title: 'Precio inválido',
        message: 'El plan no tiene precio. Corrige Maestros → Plan Entrenadores o elige otro plan.',
      });
      return;
    }
    setPaying(true);
    const placeholder = openWompiCheckoutPlaceholder();
    try {
      const r = await api.post('/training/coach/pay-clients-wompi', {
        user_ids: selected,
        plan_nombre: activePlan.nombre,
      });
      const data = r.data?.data;
      const opened = data?.payment_url
        ? navigateWompiCheckout(placeholder, data.payment_url)
        : false;
      if (opened) {
        emitToast({
          type: 'success',
          title: 'Wompi abierto',
          message: `${data.plan_nombre || 'Plan'} · ${formatCop(data.amount_cop)} (${data.client_count} cliente(s)). Cuando el super admin reciba el pago, activará al cliente.`,
        });
      } else {
        try { placeholder?.close?.(); } catch { /* noop */ }
        emitToast({
          type: 'error',
          title: 'Sin enlace Wompi',
          message: data?.warning
            || 'No hay enlace Wompi de Training. Super admin: Maestros → Enlaces de pago.',
        });
      }
    } catch (e) {
      try { placeholder?.close?.(); } catch { /* noop */ }
      emitToast({
        type: 'error',
        title: 'Error',
        message: e.response?.data?.error || 'Error iniciando pago',
      });
    } finally {
      setPaying(false);
    }
  };

  const renderClientRow = (c, { showCheckbox = false } = {}) => (
    <li key={c.user_id} className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 last:border-b-0">
      {showCheckbox ? (
        <input
          type="checkbox"
          checked={selected.includes(c.user_id)}
          onChange={() => toggle(c.user_id)}
        />
      ) : (
        <span className="w-4 shrink-0" aria-hidden />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-stone-900 truncate">{c.nombre}</p>
        <p className="text-xs text-stone-600 truncate">{c.email}</p>
        {c.wompi_reference ? (
          <p className="text-xs text-stone-500 font-mono mt-0.5 truncate">
            Ref. Wompi: {c.wompi_reference}
          </p>
        ) : null}
      </div>
      {showCheckbox ? (
        <span className="text-xs text-stone-600 hidden sm:inline">{formatCop(unitPrice)}</span>
      ) : null}
      <span className="text-xs text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full shrink-0">
        {c.estado}
      </span>
    </li>
  );

  return (
    <div className="dashboard-main-view training-coach-view training-payment-panel training-light-island surface-readable d28d-admin-surface max-w-3xl mx-auto p-5 space-y-4">
      <div className="card ui-light-panel p-5 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-stone-900">
            Activar clientes (Wompi — entrenador)
          </h2>
          <p className="text-sm text-stone-600 mt-1">
            Elige el plan, selecciona clientes y pulsa pagar: se abre el enlace Wompi configurado por el super admin
            (Maestros → Enlaces de pago → TRAINING). El super admin activa al cliente cuando reciba el dinero.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" className="btn-secondary text-sm" onClick={load} disabled={loading}>
            Actualizar
          </button>
          {onBack ? (
            <button type="button" className="btn-secondary text-sm" onClick={onBack}>
              Volver
            </button>
          ) : null}
        </div>
      </div>

      {availablePlans.length > 0 || pricing ? (
        <div className="rounded-xl border border-stone-200 bg-slate-50 p-4 space-y-3">
          {pricing && pricing.wompi_ready === false ? (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 m-0">
              Falta el enlace Wompi de Training. Super admin: Maestros → Enlaces de pago → TRAINING.
            </p>
          ) : null}
          <label className="block text-sm font-semibold text-stone-900">
            Plan a pagar por cliente
            <select
              className="input mt-1.5 w-full"
              value={selectedPlan || activePlan?.nombre || ''}
              onChange={(e) => setSelectedPlan(e.target.value)}
            >
              {availablePlans.length ? availablePlans.map((p) => (
                <option key={p.nombre} value={p.nombre}>
                  {p.nombre}
                  {' · '}
                  {monthsLabel(p.months || p.cycles_count)}
                  {' · '}
                  {formatCop(p.precio_mensual)}
                </option>
              )) : (
                <option value={pricing?.plan_nombre || ''}>
                  {pricing?.plan_nombre || 'Plan'}
                  {' · '}
                  {formatCop(pricing?.unit_price_cop)}
                </option>
              )}
            </select>
          </label>
          {activePlan ? (
            <p className="text-sm text-stone-700 m-0">
              Precio unitario: <span className="font-semibold">{formatCop(unitPrice)}</span>
              {' · '}
              Vigencia: <span className="font-semibold">{monthsLabel(activePlan.months || activePlan.cycles_count || 1)}</span>
            </p>
          ) : null}
          {unitPrice <= 0 ? (
            <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 m-0">
              El plan seleccionado no tiene precio válido. Corrige el precio en Maestros → Plan Entrenadores.
            </p>
          ) : null}
        </div>
      ) : null}

      {loading ? <p className="text-sm text-stone-600">Cargando…</p> : null}

      {!loading && !clients.length ? (
        <p className="text-sm text-stone-600">No tienes clientes pendientes de activación.</p>
      ) : null}

      {!loading && clientDirectClients.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-stone-900">
            Pago directo del cliente (Wompi)
          </h3>
          <p className="text-xs text-stone-600">
            Estos usuarios pagan ellos mismos. El super admin los activa cuando reciba el dinero.
          </p>
          <ul className="rounded-xl border border-stone-200 overflow-hidden bg-white">
            {clientDirectClients.map((c) => renderClientRow(c))}
          </ul>
        </div>
      ) : null}

      {!loading && coachPayClients.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-stone-900">
            Pago por entrenador (batch Wompi)
          </h3>
          <ul className="rounded-xl border border-stone-200 overflow-hidden bg-white">
            {coachPayClients.map((c) => renderClientRow(c, { showCheckbox: true }))}
          </ul>
        </div>
      ) : null}

      {coachPayClients.length > 0 ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="button"
            className="btn-primary w-full sm:w-auto"
            disabled={paying || !selected.length || !activePlan?.nombre || pricing?.wompi_ready === false}
            onClick={payWompi}
          >
            {paying ? 'Abriendo Wompi…' : `Pagar (${selected.length}) vía Wompi`}
          </button>
          {selected.length > 0 ? (
            <div className="text-sm text-stone-700">
              <p className="font-semibold text-stone-900 m-0">
                Total a pagar: {formatCop(totalSelected)}
              </p>
              <p className="text-xs text-stone-600 m-0 mt-1">
                Wompi abre el enlace configurado por el super admin; el monto en caja es el del datáfono, no un recálculo de la app.
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
      </div>
    </div>
  );
}
